"use client"

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  AlertTriangle,
  ArrowLeftRight,
  CalendarRange,
  Check,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Store,
  Undo2,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "@/components/ui/use-toast";
import { MESSAGES } from "../utils/messages";
import {
  conflictKey,
  dropStagedCampaigns,
  mergePendingRemoval,
  removableCampaignsOf,
  hasGradeConflict,
  REMOVABLE_CONFLICT_TYPES,
  type PendingRemoval,
} from "../utils/conflict-resolution";
import {
  conflictGradesLabel,
  conflictPeriodLabel,
  conflictStoresLabel,
} from "../utils/conflict-scope";
import type { CampaignConflictItem, ConflictingCampaign } from "@/@types/campaign-conflict";

// Re-export para compatibilidade com quem importa o tipo do modal.
export type { CampaignConflictItem };

const ROWS_PER_PAGE = 6;

const CONFLICT_MODAL_TITLE = "Produtos já cadastrados em outras campanhas";
const CONFLICT_MODAL_DESCRIPTION =
  "Os produtos abaixo já estão em outras campanhas ativas no mesmo período. Um produto não pode estar em múltiplas campanhas simultaneamente.";
const CONFLICT_GRADE_DESCRIPTION =
  "O bloqueio é por sobreposição de grade: as campanhas abaixo já alcançam a cor/tamanho escolhida no mesmo período. Grades diferentes do mesmo produto podem conviver — ajuste a cor ou o tamanho para continuar.";

const conflictDescription = (conflicts: CampaignConflictItem[]): string =>
  hasGradeConflict(conflicts)
    ? CONFLICT_GRADE_DESCRIPTION
    : CONFLICT_MODAL_DESCRIPTION;

interface ConflictingCampaignLineProps {
  campaign: ConflictingCampaign;
}

function ConflictingCampaignLine({ campaign }: ConflictingCampaignLineProps): JSX.Element {
  const grades: string | null = conflictGradesLabel(campaign);
  const stores: string | null = conflictStoresLabel(campaign);

  return (
    <li className="space-y-1 px-3 py-2 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <Badge variant="destructive" className="shrink-0 px-1.5 py-0 text-[10px]">
          {campaign.type}
        </Badge>
        <span className="shrink-0 font-semibold">#{campaign.code}</span>
        {campaign.description && (
          <span className="truncate text-muted-foreground" title={campaign.description}>
            · {campaign.description}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {grades && (
          <Badge variant="outline" className="whitespace-normal break-words px-1.5 py-0 text-[10px]">
            {grades}
          </Badge>
        )}
        {stores && (
          <span className="flex items-center gap-1" data-testid="conflict-stores">
            <Store className="h-3 w-3 shrink-0" aria-hidden />
            {stores}
          </span>
        )}
        <span className="flex items-center gap-1 whitespace-nowrap" data-testid="conflict-period">
          <CalendarRange className="h-3 w-3 shrink-0" aria-hidden />
          {conflictPeriodLabel(campaign)}
        </span>
      </div>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Tabela de conflitos (apresentacional, reusada pelos dois modos)            */
/* -------------------------------------------------------------------------- */

interface ConflictListProps {
  conflicts: CampaignConflictItem[];
  onRemove?: (conflict: CampaignConflictItem) => void;
  onDiscard?: (conflict: CampaignConflictItem) => void;
  /** Map itemCode → conflictKeys já marcados para remoção (mostra estado resolvido). */
  stagedByItem?: Map<string, Set<string>>;
  /** Desfaz a marcação de remoção de um item. */
  onUndoRemove?: (conflict: CampaignConflictItem) => void;
  /** Itens que NÃO podem ser removidos da anterior (ex.: mínimo de 2): força descarte. */
  unresolvableItemCodes?: Set<string>;
}

function ConflictList({
  conflicts,
  onRemove,
  onDiscard,
  stagedByItem,
  onUndoRemove,
  unresolvableItemCodes,
}: ConflictListProps) {
  const [page, setPage] = useState(1);
  const showActions = Boolean(onRemove || onDiscard);

  const total = conflicts.length;
  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
  // Clampa a página (a lista pode encolher conforme os conflitos são resolvidos).
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * ROWS_PER_PAGE;
  const visibleConflicts = conflicts.slice(start, start + ROWS_PER_PAGE);

  const resolvedCount = stagedByItem
    ? conflicts.filter((c) => {
        const keys = stagedByItem.get(c.itemCode);
        return (
          !!keys &&
          c.conflictingCampaigns.every((camp) => keys.has(conflictKey(camp)))
        );
      }).length
    : 0;
  const pendingCount = total - resolvedCount;

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">
        {resolvedCount > 0 ? (
          <>
            {pendingCount} pendente{pendingCount !== 1 ? "s" : ""}
            <span className="text-muted-foreground font-normal">
              {" "}
              · {resolvedCount} marcado{resolvedCount !== 1 ? "s" : ""} para remoção
            </span>
          </>
        ) : (
          <>
            {total} produto{total !== 1 ? "s" : ""} em conflito
          </>
        )}
      </p>

      <div className="space-y-3">
        {visibleConflicts.map((conflict) => {
          const stagedKeys = stagedByItem?.get(conflict.itemCode);
          const remaining = stagedKeys
            ? conflict.conflictingCampaigns.filter((c) => !stagedKeys.has(conflictKey(c)))
            : conflict.conflictingCampaigns;
          const isStaged = !!stagedKeys && stagedKeys.size > 0;

          // Totalmente resolvido por remoção → card de confirmação + Desfazer.
          if (isStaged && remaining.length === 0) {
            return (
              <div
                key={conflict.itemCode}
                className="flex items-center justify-between gap-3 rounded-lg border border-green-300 bg-green-50/60 dark:bg-green-950/20 px-4 py-3"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Check className="w-5 h-5 text-green-600 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold leading-tight break-words">
                      {conflict.itemName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      SKU {conflict.itemCode} · será removido da(s) campanha(s)
                      anterior(es) ao criar
                    </div>
                  </div>
                </div>
                {onUndoRemove && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onUndoRemove(conflict)}
                    className="h-8 gap-1 shrink-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
                  >
                    <Undo2 className="w-4 h-4" />
                    Desfazer
                  </Button>
                )}
              </div>
            );
          }

          const isUnresolvable = unresolvableItemCodes?.has(conflict.itemCode) ?? false;
          const removable =
            !isUnresolvable &&
            remaining.some((c) => REMOVABLE_CONFLICT_TYPES.includes(c.type));
          const hasNonRemovable = remaining.some((c) => !REMOVABLE_CONFLICT_TYPES.includes(c.type));
          const onlyNonRemovable = !removable && hasNonRemovable;

          return (
            <div
              key={conflict.itemCode}
              className="rounded-lg border bg-card overflow-hidden"
            >
              {/* Cabeçalho — o produto é o título da seção */}
              <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-2.5">
                <div className="min-w-0">
                  <h4 className="font-semibold leading-tight break-words">
                    {conflict.itemName}
                  </h4>
                  <span className="text-xs font-mono text-muted-foreground">
                    SKU {conflict.itemCode}
                  </span>
                </div>
                <span className="shrink-0 inline-flex items-center rounded-full border border-red-200 bg-red-50 dark:bg-red-950/30 px-2 py-0.5 text-xs font-medium text-red-600 dark:text-red-400">
                  {remaining.length} {remaining.length === 1 ? "campanha" : "campanhas"}
                </span>
              </div>

              {/* Problema — onde já está cadastrado */}
              <div className="px-4 pb-3 space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">
                  Já cadastrado em
                </p>
                <ul className="rounded-md border divide-y">
                  {remaining.map((campaign, idx) => (
                    <ConflictingCampaignLine
                      key={`${campaign.code}-${campaign.storeCode}-${campaign.listNumber ?? ""}-${idx}`}
                      campaign={campaign}
                    />
                  ))}
                </ul>
                {isStaged && onUndoRemove && (
                  <p className="flex items-center gap-1 text-xs text-green-700 dark:text-green-500">
                    <Check className="w-3.5 h-3.5" />
                    Parte das campanhas já marcada para remoção.
                    <button
                      type="button"
                      onClick={() => onUndoRemove(conflict)}
                      className="underline underline-offset-2 hover:no-underline"
                    >
                      Desfazer
                    </button>
                  </p>
                )}
                {isUnresolvable && (
                  <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-500">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      Não dá para remover desta campanha (ela ficaria com menos de 2
                      produtos ativos). Use <strong>Descartar item</strong> ou ajuste
                      a campanha anterior manualmente.
                    </span>
                  </div>
                )}
                {!isUnresolvable && removable && hasNonRemovable && (
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    As campanhas PRO013 (departamento/grupo) serão mantidas e
                    precisam de ajuste manual.
                  </p>
                )}
              </div>

              {/* Ação — zona separada, primária à direita */}
              {showActions && (
                <div className="flex items-center justify-end gap-3 border-t bg-muted/30 px-4 py-2.5">
                  {onDiscard && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDiscard(conflict)}
                      className="h-8 gap-1 text-muted-foreground hover:bg-transparent hover:text-destructive"
                    >
                      <X className="w-4 h-4" />
                      Descartar item
                    </Button>
                  )}
                  {onRemove && (
                    <RemoveButton
                      disabled={!removable}
                      onlyNonRemovable={onlyNonRemovable}
                      onClick={() => onRemove(conflict)}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setPage(currentPage - 1)}
            className="gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {currentPage} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage(currentPage + 1)}
            className="gap-1"
          >
            Próxima
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

interface RemoveButtonProps {
  disabled: boolean;
  onlyNonRemovable: boolean;
  onClick: () => void;
}

function RemoveButton({ disabled, onlyNonRemovable, onClick }: RemoveButtonProps) {
  const button = (
    <Button
      variant="default"
      size="sm"
      disabled={disabled}
      onClick={onClick}
      className="h-8 gap-1"
    >
      <ArrowLeftRight className="w-4 h-4" />
      Remover da anterior
    </Button>
  );

  if (!onlyNonRemovable) return button;

  return (
    <TooltipProvider>
      <Tooltip>
        {/* span wrapper: tooltip precisa de um alvo habilitado para receber hover */}
        <TooltipTrigger asChild>
          <span className="inline-block">{button}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          Campanhas por departamento/grupo (PRO013) não permitem remover um único
          produto. Ajuste a campanha PRO013 manualmente ou descarte o item.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* -------------------------------------------------------------------------- */
/* Conteúdo do modal de RESOLUÇÃO (renderizado pelo <Modal/> global)          */
/* -------------------------------------------------------------------------- */

export interface CampaignConflictModalContentProps {
  /** Conflitos detectados ao tentar criar a campanha (semeia o estado interno). */
  initialConflicts: CampaignConflictItem[];
  /** Remove o SKU dos itens/listas locais da nova campanha (ação "Descartar"). */
  discardItemLocally: (itemCode: string) => void;
  /** Estagia a remoção das campanhas anteriores (aplicada só ao criar). */
  onStageRemoval: (removal: CampaignConflictItem) => void;
  /** Desfaz a marcação de remoção de um item. */
  onUnstageRemoval: (itemCode: string) => void;
  /** Mantém o `conflicts` do form sincronizado (steps 4/5 + gate de submit). */
  onConflictsChange: (conflicts: CampaignConflictItem[]) => void;
  /** "Editar lista de produtos": volta para a etapa de produtos (o modal já é fechado). */
  onGoBack: () => void;
  /** Itens que o backend não conseguiu remover (mín. 2): força descarte. */
  unresolvableItemCodes?: string[];
}

/**
 * Conteúdo do modal de resolução de conflito, exibido via `useDialogModal`
 * (singleton global) — igual aos demais modais do sistema.
 *
 * "Remover da anterior" apenas MARCA a remoção (estagia) — nada é alterado no
 * backend até criar a campanha; o item marcado mostra um estado resolvido com
 * "Desfazer", em vez de sumir. "Descartar" remove o item da nova campanha.
 */
export function CampaignConflictModalContent({
  initialConflicts,
  discardItemLocally,
  onStageRemoval,
  onUnstageRemoval,
  onConflictsChange,
  onGoBack,
  unresolvableItemCodes,
}: CampaignConflictModalContentProps) {
  const { handleModal } = useDialogModal();
  const unresolvableSet = new Set(unresolvableItemCodes ?? []);
  const closeModal = () =>
    handleModal({ isOpen: false, element: null, title: "", description: "" });

  // Lista completa exibida (estável) + decisões locais por item.
  const [items] = useState(initialConflicts);
  const [removed, setRemoved] = useState<PendingRemoval[]>([]);
  const [discarded, setDiscarded] = useState<string[]>([]);

  const stagedByItem = new Map(
    removed.map((r) => [r.itemCode, new Set(r.conflictingCampaigns.map(conflictKey))]),
  );
  const visibleItems = items.filter((item) => !discarded.includes(item.itemCode));

  // Conflitos ainda pendentes (sem as campanhas marcadas e sem os descartados):
  // é o que o form usa pro gate de submit / steps.
  const syncPending = (nextRemoved: PendingRemoval[], nextDiscarded: string[]) => {
    const visible = items.filter((item) => !nextDiscarded.includes(item.itemCode));
    onConflictsChange(dropStagedCampaigns(visible, nextRemoved));
  };

  const handleRemove = (conflict: CampaignConflictItem) => {
    const removable = removableCampaignsOf(conflict);
    if (removable.length === 0) return;
    const nextRemoved = mergePendingRemoval(removed, {
      itemCode: conflict.itemCode,
      itemName: conflict.itemName,
      conflictingCampaigns: removable,
    });
    setRemoved(nextRemoved);
    onStageRemoval({ ...conflict, conflictingCampaigns: removable });
    syncPending(nextRemoved, discarded);
    toast(MESSAGES.RESOLVE_CONFLICT.SUCCESS);
  };

  const handleUndoRemove = (conflict: CampaignConflictItem) => {
    const nextRemoved = removed.filter((r) => r.itemCode !== conflict.itemCode);
    setRemoved(nextRemoved);
    onUnstageRemoval(conflict.itemCode);
    syncPending(nextRemoved, discarded);
  };

  const handleDiscard = (conflict: CampaignConflictItem) => {
    const nextDiscarded = [...discarded, conflict.itemCode];
    setDiscarded(nextDiscarded);
    discardItemLocally(conflict.itemCode);
    syncPending(removed, nextDiscarded);
    toast(MESSAGES.RESOLVE_CONFLICT.INFO!);
  };

  return (
    <div className="w-[min(92vw,780px)] space-y-4">
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
        {conflictDescription(visibleItems)}
      </p>

      <ConflictList
        conflicts={visibleItems}
        stagedByItem={stagedByItem}
        unresolvableItemCodes={unresolvableSet}
        onRemove={handleRemove}
        onUndoRemove={handleUndoRemove}
        onDiscard={handleDiscard}
      />

      <div className="flex items-center justify-between gap-2 pt-3 border-t">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            closeModal();
            onGoBack();
          }}
          className="gap-1 text-muted-foreground"
        >
          <Pencil className="w-4 h-4" />
          Editar lista de produtos
        </Button>
        <Button variant="outline" onClick={closeModal}>
          Fechar
        </Button>
      </div>
    </div>
  );
}

/** Título usado ao abrir o modal de resolução via `handleModal`. */
CampaignConflictModalContent.title = CONFLICT_MODAL_TITLE;

/* -------------------------------------------------------------------------- */
/* Modal SOMENTE-LEITURA (fluxo de edição — Pro014ItemsModal)                 */
/* -------------------------------------------------------------------------- */

interface CampaignConflictModalProps {
  isOpen: boolean;
  conflicts: CampaignConflictItem[];
  onClose: () => void;
  onGoBack: () => void;
}

/**
 * Exibição somente-leitura do conflito (sem ações de resolução). Mantida para
 * o fluxo de edição (`Pro014ItemsModal`), que apenas mostra os conflitos.
 */
export function CampaignConflictModal({
  isOpen,
  conflicts,
  onClose,
  onGoBack,
}: CampaignConflictModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-2xl w-[min(92vw,780px)] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            {CONFLICT_MODAL_TITLE}
          </DialogTitle>
          <DialogDescription>{conflictDescription(conflicts)}</DialogDescription>
        </DialogHeader>

        <ConflictList conflicts={conflicts} />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={onGoBack}>Voltar e Ajustar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
